import {
  HexCoord,
  VertexCoord,
  EdgeCoord,
  HexTile,
  TerrainType,
  Harbor,
  HarborType,
  STANDARD_BOARD_ROWS,
  EXTENDED_BOARD_ROWS,
  STANDARD_TERRAIN_COUNTS,
  EXTENDED_TERRAIN_COUNTS,
  STANDARD_NUMBER_TOKENS,
  DEVELOPMENT_CARD_COUNTS,
  EXTENDED_DEVELOPMENT_CARD_COUNTS,
  DevelopmentCardType,
} from './types';

// 配列をシャッフル
export function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Hex座標から画面座標への変換 (pointy-top hexagon, 辺同士が接する)
export function hexToPixel(coord: HexCoord, size: number): { x: number; y: number } {
  // pointy-top: 角が左右、辺が上下
  const x = size * Math.sqrt(3) * (coord.q + coord.r / 2);
  const y = size * (3 / 2) * coord.r;
  return { x, y };
}

// 画面座標からHex座標への変換 (pointy-top hexagon)
export function pixelToHex(x: number, y: number, size: number): HexCoord {
  const q = (Math.sqrt(3) / 3 * x - 1 / 3 * y) / size;
  const r = (2 / 3 * y) / size;
  return hexRound({ q, r });
}

// Hex座標を整数に丸める
function hexRound(coord: { q: number; r: number }): HexCoord {
  const s = -coord.q - coord.r;
  let rq = Math.round(coord.q);
  let rr = Math.round(coord.r);
  const rs = Math.round(s);
  
  const qDiff = Math.abs(rq - coord.q);
  const rDiff = Math.abs(rr - coord.r);
  const sDiff = Math.abs(rs - s);
  
  if (qDiff > rDiff && qDiff > sDiff) {
    rq = -rr - rs;
  } else if (rDiff > sDiff) {
    rr = -rq - rs;
  }
  
  return { q: rq, r: rr };
}

// Hexの6つの隣接Hex
export function getHexNeighbors(coord: HexCoord): HexCoord[] {
  const directions = [
    { q: 1, r: 0 },   // 東
    { q: 1, r: -1 },  // 北東
    { q: 0, r: -1 },  // 北西
    { q: -1, r: 0 },  // 西
    { q: -1, r: 1 },  // 南西
    { q: 0, r: 1 },   // 南東
  ];
  return directions.map(d => ({ q: coord.q + d.q, r: coord.r + d.r }));
}

// Vertex座標を正規化（同じ頂点を同じ座標で表現）
export function normalizeVertex(vertex: VertexCoord): VertexCoord {
  // 各頂点は3つのHexから参照されるので、代表的な1つに正規化
  // ここでは最も小さいq, rを持つHexを選ぶ
  return vertex; // 簡略化のためそのまま返す
}

// 頂点の隣接頂点を取得
export function getAdjacentVertices(vertex: VertexCoord): VertexCoord[] {
  const { hex, direction } = vertex;
  if (direction === 'N') {
    return [
      { hex: { q: hex.q, r: hex.r - 1 }, direction: 'S' },
      { hex: { q: hex.q + 1, r: hex.r - 1 }, direction: 'S' },
      { hex, direction: 'S' } // 下隣接は不正確、修正が必要
    ];
  } else {
    return [
      { hex: { q: hex.q, r: hex.r + 1 }, direction: 'N' },
      { hex: { q: hex.q - 1, r: hex.r + 1 }, direction: 'N' },
      { hex, direction: 'N' }
    ];
  }
}

// Hexの6つの頂点を取得 (pointy-top)
export function getHexVertices(hex: HexCoord): VertexCoord[] {
  return [
    { hex, direction: 'N' },
    { hex, direction: 'S' },
    { hex: { q: hex.q + 1, r: hex.r - 1 }, direction: 'S' },
    { hex: { q: hex.q + 1, r: hex.r }, direction: 'N' },
    { hex: { q: hex.q, r: hex.r + 1 }, direction: 'N' },
    { hex: { q: hex.q - 1, r: hex.r + 1 }, direction: 'N' },
  ];
}

// Hexの6つの辺を取得
export function getHexEdges(hex: HexCoord): EdgeCoord[] {
  return [
    { hex, direction: 'NE' },
    { hex, direction: 'E' },
    { hex, direction: 'SE' },
    { hex: { q: hex.q - 1, r: hex.r + 1 }, direction: 'NE' },
    { hex: { q: hex.q - 1, r: hex.r }, direction: 'E' },
    { hex: { q: hex.q, r: hex.r - 1 }, direction: 'SE' },
  ];
}

// 頂点座標からピクセル座標への変換 (pointy-top)
export function vertexToPixel(vertex: VertexCoord, size: number): { x: number; y: number } {
  const hexPos = hexToPixel(vertex.hex, size);
  
  if (vertex.direction === 'N') {
    return { x: hexPos.x, y: hexPos.y - size };
  } else { // 'S'
    return { x: hexPos.x, y: hexPos.y + size };
  }
}

// 辺座標からピクセル座標への変換 (pointy-top)
export function edgeToPixel(edge: EdgeCoord, size: number): { x: number; y: number } {
  const hexPos = hexToPixel(edge.hex, size);
  const w = size * Math.sqrt(3) / 2; // 横幅の半分
  
  switch (edge.direction) {
    case 'NE':
      return { x: hexPos.x + w / 2, y: hexPos.y - size * 3 / 4 };
    case 'E':
      return { x: hexPos.x + w, y: hexPos.y };
    case 'SE':
      return { x: hexPos.x + w / 2, y: hexPos.y + size * 3 / 4 };
    case 'SW':
      return { x: hexPos.x - w / 2, y: hexPos.y + size * 3 / 4 };
    case 'W':
      return { x: hexPos.x - w, y: hexPos.y };
    case 'NW':
      return { x: hexPos.x - w / 2, y: hexPos.y - size * 3 / 4 };
    default:
      return hexPos;
  }
}

// 頂点に接続する辺を取得
export function getVertexEdges(vertex: VertexCoord): EdgeCoord[] {
  const { hex, direction } = vertex;
  if (direction === 'N') {
    return [
      { hex, direction: 'NE' },
      { hex: { q: hex.q - 1, r: hex.r }, direction: 'NE' },
      { hex: { q: hex.q, r: hex.r - 1 }, direction: 'E' },
    ];
  } else {
    return [
      { hex, direction: 'SE' },
      { hex: { q: hex.q - 1, r: hex.r + 1 }, direction: 'NE' },
      { hex, direction: 'E' },
    ];
  }
}

// 2つの座標が同じか比較
export function hexEquals(a: HexCoord, b: HexCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

export function vertexEquals(a: VertexCoord, b: VertexCoord): boolean {
  return hexEquals(a.hex, b.hex) && a.direction === b.direction;
}

export function edgeEquals(a: EdgeCoord, b: EdgeCoord): boolean {
  return hexEquals(a.hex, b.hex) && a.direction === b.direction;
}

// ボードのHex座標を生成 (カタンの標準配置: 3-4-5-4-3)
export function generateBoardHexes(mode: 'standard' | 'extended'): HexCoord[] {
  const rows = mode === 'standard' ? STANDARD_BOARD_ROWS : EXTENDED_BOARD_ROWS;
  const hexes: HexCoord[] = [];
  const midRow = Math.floor(rows.length / 2);
  
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const tilesInRow = rows[rowIndex];
    const r = rowIndex - midRow;
    
    // pointy-top hexagonの場合: 中央の行(最大タイル数)を基準にqを計算
    // 上半分(r < 0)はqオフセットなし、下半分(r > 0)はqを左にシフト
    const qStart = -Math.floor(tilesInRow / 2);
    
    for (let i = 0; i < tilesInRow; i++) {
      let q = qStart + i;
      // axial座標系での補正: rがマイナスの行はqを右にシフト
      if (r < 0) {
        q = q - r;
      }
      hexes.push({ q, r });
    }
  }
  
  return hexes;
}

// 地形タイルを生成
export function generateHexTiles(mode: 'standard' | 'extended'): HexTile[] {
  const hexCoords = generateBoardHexes(mode);
  
  // 地形をシャッフル
  const terrains: TerrainType[] = [];
  const counts = mode === 'extended' ? EXTENDED_TERRAIN_COUNTS : STANDARD_TERRAIN_COUNTS;
  
  for (const [terrain, count] of Object.entries(counts)) {
    for (let i = 0; i < count; i++) {
      terrains.push(terrain as TerrainType);
    }
  }
  
  const shuffledTerrains = shuffle(terrains);
  
  // 数値トークンをシャッフル（砂漠以外に配置）
  let numberTokens = [...STANDARD_NUMBER_TOKENS];
  if (mode === 'extended') {
    // 拡張版用の追加数値
    numberTokens = [...numberTokens, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12];
  }
  const shuffledNumbers = shuffle(numberTokens);
  
  // タイルを生成
  const tiles: HexTile[] = [];
  let numberIndex = 0;
  
  for (let i = 0; i < hexCoords.length; i++) {
    const terrain = shuffledTerrains[i];
    const isDesert = terrain === 'desert';
    
    tiles.push({
      coord: hexCoords[i],
      terrain,
      number: isDesert ? null : shuffledNumbers[numberIndex++],
      hasRobber: isDesert // 最初は砂漠に盗賊
    });
  }
  
  return tiles;
}

// スタンダード版の港の配置を生成
export function generateHarbors(mode: 'standard' | 'extended'): Harbor[] {
  const harbors: Harbor[] = [];

  // 港の種類: 4つの汎用港(3:1)と5つの専門港(2:1)
  const baseHarborTypes: HarborType[] = [
    'general', 'general', 'general', 'general',
    'brick', 'wood', 'wheat', 'sheep', 'ore'
  ];
  // 拡張版は港を2つ追加（一般港 + 羊毛港）
  const harborTypes: HarborType[] = mode === 'extended'
    ? shuffle([...baseHarborTypes, 'general', 'sheep'])
    : shuffle([...baseHarborTypes]);

  const hexCoords = generateBoardHexes(mode);
  const hexSet = new Set(hexCoords.map(h => `${h.q},${h.r}`));
  const boundaryEdges: EdgeCoord[] = [];
  const edgeDirections: EdgeCoord['direction'][] = ['NE', 'E', 'SE'];

  const getNeighborAcrossEdge = (hex: HexCoord, direction: EdgeCoord['direction']): HexCoord => {
    switch (direction) {
      case 'NE':
        return { q: hex.q + 1, r: hex.r - 1 };
      case 'E':
        return { q: hex.q + 1, r: hex.r };
      case 'SE':
        return { q: hex.q, r: hex.r + 1 };
      default:
        return hex;
    }
  };

  for (const hex of hexCoords) {
    for (const direction of edgeDirections) {
      const neighbor = getNeighborAcrossEdge(hex, direction);
      if (!hexSet.has(`${neighbor.q},${neighbor.r}`)) {
        boundaryEdges.push({ hex, direction });
      }
    }
  }

  const candidates = shuffle(boundaryEdges);
  const selectedEdges = candidates.slice(0, harborTypes.length);

  for (let i = 0; i < selectedEdges.length && i < harborTypes.length; i++) {
    const type = harborTypes[i];
    harbors.push({
      edgeCoord: selectedEdges[i],
      type,
      ratio: type === 'general' ? 3 : 2
    });
  }

  return harbors;
}

// 発展カードのデッキを生成
export function generateDevelopmentDeck(mode: 'standard' | 'extended' = 'standard'): DevelopmentCardType[] {
  const deck: DevelopmentCardType[] = [];
  const counts = mode === 'extended' ? EXTENDED_DEVELOPMENT_CARD_COUNTS : DEVELOPMENT_CARD_COUNTS;

  for (const [type, count] of Object.entries(counts)) {
    for (let i = 0; i < count; i++) deck.push(type as DevelopmentCardType);
  }

  return shuffle(deck);
}

// サイコロ2個を振る
export function rollDice(): [number, number] {
  return [
    Math.floor(Math.random() * 6) + 1,
    Math.floor(Math.random() * 6) + 1
  ];
}

// 頂点に隣接する全てのHex座標を取得
export function getVertexAdjacentHexes(vertex: VertexCoord): HexCoord[] {
  const { hex, direction } = vertex;
  if (direction === 'N') {
    return [
      hex,
      { q: hex.q, r: hex.r - 1 },
      { q: hex.q + 1, r: hex.r - 1 }
    ];
  } else {
    return [
      hex,
      { q: hex.q, r: hex.r + 1 },
      { q: hex.q - 1, r: hex.r + 1 }
    ];
  }
}

// 辺の両端の頂点を取得
export function getEdgeVertices(edge: EdgeCoord): [VertexCoord, VertexCoord] {
  const { hex, direction } = edge;
  switch (direction) {
    case 'NE':
      return [
        { hex, direction: 'N' },
        { hex: { q: hex.q + 1, r: hex.r - 1 }, direction: 'S' }
      ];
    case 'E':
      return [
        { hex: { q: hex.q + 1, r: hex.r - 1 }, direction: 'S' },
        { hex: { q: hex.q + 1, r: hex.r }, direction: 'N' }
      ];
    case 'SE':
      return [
        { hex: { q: hex.q + 1, r: hex.r }, direction: 'N' },
        { hex, direction: 'S' }
      ];
    default:
      return [
        { hex, direction: 'N' },
        { hex, direction: 'S' }
      ];
  }
}
